import React from 'react';
import { observer, inject } from 'mobx-react';
import { useState } from 'react';
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { delegateEmployeesFromSoxPanel } from '../../helper/constants';
import { Link, Typography, IconButton } from '@material-ui/core';
import { HtmlTooltip } from '../../components/HtmlTooltip/HtmlTooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ReplayIcon from '@material-ui/icons/Replay';
import MaterialTable from "material-table";
import { chameleonSystem } from '../../helper/constants';
import api from "../../utils/api";
import EventBus from "../../helper/event.bus";
import {eventBusMessages, eventTypesForBus} from "../../helper/enums";

const QarAdminEmployeeInfoRow = inject('RootStore')(observer(({ RootStore, ...props }) => {

    const emp = props.emp;
    const isUnmatchedAccount = props.isUnmatchedAccount;

    const [delegatedEmployee, setDelegatedEmployee] = useState({});

    const [showModal, setShowModal] = useState(false);

    const getEmployeesList = () => RootStore.qarStore.allActiveSnpEmployees.map(m => ({
        key: m.id,
        text: (m.firstName ?? "") + " " + (m.middleName ?? "") + " " + (m.lastName ?? ""),
        value: m.id
    }));

    const actionMaps = {
        0: "Missing Action",
        1: "Approved",
        2: "Revoked",
        3: "Changed",
        4: "Delegated to"
    };

    const shouldIncludeIsChameleonAdmin = () => isUnmatchedAccount ? undefined : RootStore.qarStore.employeesByManagerReview.employees.find(x => x.isChameleonAdmin);

    const renderIsChameleonRow = (emp) => shouldIncludeIsChameleonAdmin() !== undefined ?
        <TableCell align="left">
            {emp.isChameleonAdmin ? <i className="fas fa-check"></i> : <i className="fas fa-minus"></i>}
        </TableCell> : <></>;

    const handleSaveClick = () => {
        RootStore.qarStore.delegateEmployeesFromAdminPanel(delegatedEmployee);
        setShowModal(false);
    }

    const useStyles = makeStyles((theme) => ({
        formControl: {
            margin: theme.spacing(1),
            minWidth: '15em',
        },
        selectEmpty: {
            marginTop: theme.spacing(2),
        },
    }));

    const classes = useStyles();

    const handleDelegatedToChange = (ev, data) => {
        ev.preventDefault();

        if (!data) {
            return;
        }

        let managerForDelegation = RootStore.qarStore.allActiveSnpEmployees.find(e => e.id === data.value);

        let requestData = {
            delegatedManagerId: managerForDelegation.id,
            quarter: RootStore.qarStore.currentQuarter,
            year: RootStore.qarStore.currentYear,
            employeeId: emp.id,
            initiatorId: RootStore.userStore.currentUserID
        };
        setDelegatedEmployee({ ...requestData });
    };

    const tableOptions = {
        filtering: true,
        grouping: false,
        draggable: false,
        paging: true,
        search: true,
        toolbar: true,
        showTitle: false,
        sorting: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 15]
    };

    const handleRoleClick = async (event, role) => {
        event.preventDefault(); // this prevents appending hashtag to the url
        RootStore.qarStore.currentlyReviewedRole = role.name
        await RootStore.qarFormStore.loadRolePermissionInfo(role.id);
        RootStore.uiStateStore.setModalContent(renderRolePermissionGroupsTable());
        RootStore.uiStateStore.setModalOpen(true);
    }

    const renderRolePermissionGroupsTable = () => {
        const rolePermissionInfo = RootStore.qarFormStore.rolePermissionInfo;

        if (rolePermissionInfo === undefined) {
            return;
        }

        const columns = [
            { title: 'Name', field: 'name', defaultSort: "asc" },
            { title: 'Description', field: 'description' },
        ]

        if (rolePermissionInfo.systemName === chameleonSystem) {
            columns.push({ title: 'Permission Actions', field: 'permissionActions', render: (rowdata) => <>{rowdata.permissionActions?.join(', ')}</> })
        }

        return (
            <>
                <h2>Role '{RootStore.qarStore.currentlyReviewedRole}' has a total of {rolePermissionInfo.rolePermissions.length} permission {rolePermissionInfo.rolePermissions.length !== 1 ? "groups" : "group"}</h2>
                <MaterialTable
                    options={tableOptions}
                    data={rolePermissionInfo.rolePermissions}
                    columns={columns}
                />
            </>
        );
    }

    const handleUsernameClick = (event, employeeAccount) => {
        event.preventDefault(); // this prevents appending hashtag to the url
        RootStore.uiStateStore.setModalContent(renderUserPermissionGroupsTable(employeeAccount));
        RootStore.uiStateStore.setModalOpen(true);
    }

    const handleActionResetClick = async (event, rowData) => {
        event.preventDefault(); // this prevents appending hashtag to the url
        const response = await api.get(`QAR/ResetAccountReviewAction`, {
            params: {
                accountReviewId: rowData.accountReviewId,
                initiatorId: RootStore.userStore.currentUserID
            }
        });

        if (!api.responseOk(response)) {
            EventBus.publish(eventTypesForBus.resetAccountReviewActionError, 'Request error');
            return;
        }

        if(response.data.isSuccessful === false){
            EventBus.publish(eventTypesForBus.resetAccountReviewActionError, response.data.errorMessages.toString());
            return;
        }

        rowData.action.name = 0;
    }

    const renderUserPermissionGroupsTable = (employeeAccount) => {
        return (
            <>
                <h2>
                    User '{employeeAccount.username}' has a total of {employeeAccount.permissionGroups.length} permission {employeeAccount.permissionGroups.length !== 1 ? "groups" : "group"} in {RootStore.qarStore.currentlySelectedAccountForReview.name} ({RootStore.qarStore.currentlyReviewedSystemForReview.name})
                </h2>

                <MaterialTable
                    options={tableOptions}
                    data={employeeAccount.permissionGroups}
                    columns={[
                        { title: 'Name', field: 'name', defaultSort: "asc" },
                        { title: 'Description', field: 'description' }
                    ]}
                />
            </>
        );
    }

    const getTicketId = (ticketLink) => {
        if (!ticketLink) return
        const ticketId = ticketLink?.match(/[^/]*$/)
        return ticketId
    };

    return (
        <>
            <TableRow>
                <TableCell align="left">
                    {emp.fullName}<br />
                    {emp.title}<br />
                    {emp.email}
                </TableCell>
                <TableCell width="15%">
                            <Link href="#" onClick={(event) => handleUsernameClick(event, emp)}>{emp.username}</Link>
                            <HtmlTooltip
                                title={
                                    <React.Fragment>
                                        <Typography color="inherit" variant="caption">Click on username to see its permission groups.</Typography>
                                    </React.Fragment>
                                }
                            >
                                <IconButton aria-label="delete" size="small">
                                    <HelpOutlineIcon fontSize="inherit" />
                                </IconButton>
                            </HtmlTooltip>
                        </TableCell>
                <TableCell align="left">
                    {emp.roles.length > 0 && emp.roles
                    .map(role => <><Link href="#" onClick={(event) => handleRoleClick(event, role)}>{role.name}</Link>
                        <HtmlTooltip
                            title={<><Typography color="inherit" variant="caption">Click on role name to see its groups.</Typography></>}>
                            <IconButton aria-label="delete" size="small"><HelpOutlineIcon fontSize="inherit" /></IconButton>
                        </HtmlTooltip></>)
                    .reduce((previous, current) => [previous, ", ", current])}
                </TableCell>
                <TableCell align="left"><>{actionMaps[emp.action.name]}
                    {(emp.action.name != 0 && emp.action.name != 4 && !RootStore.qarStore.isManagerReviewsCompleted) ?
                        <HtmlTooltip title={<Typography color="inherit" variant="caption">Set action to Missing Action.</Typography>}>
                            <IconButton size="small" onClick={(event) => handleActionResetClick(event, emp)}>
                                <ReplayIcon fontSize="inherit"/>
                            </IconButton>
                        </HtmlTooltip>: ''}</>
                </TableCell>
                <TableCell align="left"><a href={emp.action.ticketId} target="_blank">{getTicketId(emp.action.ticketId)}</a></TableCell>
                <TableCell align="left">{emp.action.comment}</TableCell>
                {emp.action.delegatedTo ? <TableCell align="left">{emp.action.delegatedTo.fullName}</TableCell>
                    :
                    <TableCell className={""} align="left" rowSpan={1}>
                        <FormControl className={classes.formControl}>
                            <Autocomplete
                                disabled={emp.action.delegatedTo || RootStore.qarStore.isManagerReviewsCompleted}
                                debug
                                defaultValue={emp.action.delegatedTo ? getEmployeesList().filter(m => m.value === emp.action.delegatedTo.id)[0] : ""}
                                onChange={(ev, data) => {
                                    if (!emp.action.delegatedTo) {
                                        handleDelegatedToChange(ev, data)
                                    }
                                }}
                                options={getEmployeesList()}
                                getOptionLabel={(option) => option.text || ""}
                                filterOptions={(options, { inputValue }) => {
                                    const words = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
                                    return options.filter(option => {
                                        const label = (option.text || "").toLowerCase();
                                        return words.every(word => label.includes(word));
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                    </TableCell>}
                <TableCell>
                    <Button disabled={RootStore.qarStore.isManagerReviewsCompleted || emp.action.delegatedTo} variant="contained" color="primary" onClick={(ev) => setShowModal(true)}>
                        Save
                    </Button>
                </TableCell>
                {renderIsChameleonRow(emp)}
            </TableRow>
            <ConfirmationModal
                content={delegateEmployeesFromSoxPanel.modalHeader}
                header={delegateEmployeesFromSoxPanel.modalContent}
                submitFunction={handleSaveClick}
                onClose={() => setShowModal(false)}
                show={showModal}
            />
        </>
    );
}));

export default QarAdminEmployeeInfoRow;